import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { GitCompare } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageParts";
import { Spinner, Card } from "@/components/ui";

// Metric rows: [dotted profile path, EN label, AR label, formatter]
const ROWS = [
  ["structural.concrete_grade_min_MPa", "Min concrete grade", "أدنى رتبة خرسانة", (v) => `${v} MPa`],
  ["structural.steel_yield_MPa", "Steel yield fy", "إجهاد خضوع الحديد", (v) => `${v} MPa`],
  ["structural.safety_factor_dead", "γ dead load", "معامل الحمل الميت", (v) => v],
  ["structural.safety_factor_live", "γ live load", "معامل الحمل الحي", (v) => v],
  ["structural.foundation_depth_min_m", "Min foundation depth", "أدنى عمق أساس", (v) => `${v} m`],
  ["structural.concrete_cover_mm.footing_earth_cast", "Cover - footing (earth)", "غطاء القاعدة (تربة)", (v) => `${v} mm`],
  ["structural.concrete_cover_mm.beam", "Cover - beam", "غطاء الكمرة", (v) => `${v} mm`],
  ["structural.concrete_cover_mm.column", "Cover - column", "غطاء العمود", (v) => `${v} mm`],
  ["structural.concrete_cover_mm.slab", "Cover - slab", "غطاء البلاطة", (v) => `${v} mm`],
  ["structural.min_rebar_pct.slab", "ρ min - slab", "أدنى تسليح بلاطة", (v) => `${(v * 100).toFixed(2)}%`],
  ["structural.min_rebar_pct.beam", "ρ min - beam", "أدنى تسليح كمرة", (v) => `${(v * 100).toFixed(2)}%`],
  ["structural.min_rebar_pct.column", "ρ min - column", "أدنى تسليح عمود", (v) => `${(v * 100).toFixed(2)}%`],
  ["fire.fire_rating_min_h.load_bearing_wall", "Fire - bearing wall", "حريق الجدار الحامل", (v) => `${v} h`],
  ["fire.fire_rating_min_h.column", "Fire - column", "حريق العمود", (v) => `${v} h`],
  ["fire.fire_rating_min_h.stair_enclosure", "Fire - stair", "حريق الدرج", (v) => `${v} h`],
  ["insulation.wall_U_max_W_m2K", "Wall U-value max", "أقصى U للجدار", (v) => `${v} W/m²K`],
  ["insulation.roof_U_max_W_m2K", "Roof U-value max", "أقصى U للسقف", (v) => `${v} W/m²K`],
  ["insulation.window_U_max_W_m2K", "Window U-value max", "أقصى U للنافذة", (v) => `${v} W/m²K`],
  ["insulation.SHGC_max", "SHGC max", "أقصى معامل اكتساب حراري", (v) => v],
  ["electrical.voltage_V", "Mains voltage", "الجهد الكهربائي", (v) => `${v} V`],
  ["electrical.frequency_Hz", "Frequency", "التردد", (v) => `${v} Hz`],
];

function dig(obj, path) {
  return path.split(".").reduce((n, k) => (n && typeof n === "object" ? n[k] : undefined), obj);
}

export default function CodeCompare() {
  const { lang } = useAuth();
  const isAr = lang === "ar";
  const [codes, setCodes] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const list = await api.listCodeProfiles();
        const items = list.codes || list.items || [];
        setCodes(items);
        const def = items.slice(0, 3).map((c) => c.code_id);
        setSelected(def);
        const full = {};
        await Promise.all(items.map(async (c) => { full[c.code_id] = await api.getCodeProfile(c.code_id); }));
        setProfiles(full);
      } catch (e) {
        toast.error(String(e.message || e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = (id) =>
    setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.length >= 4 ? p : [...p, id]));

  if (loading) return <div className="grid min-h-[60vh] place-items-center"><Spinner size={36} className="text-primary" /></div>;

  const cols = selected.map((id) => profiles[id]).filter(Boolean);

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <PageHeader
        eyebrow={isAr ? "مقارنة الأكواد" : "Code Comparison"}
        title={isAr ? "قارن الأكواد الإنشائية جنباً إلى جنب" : "Compare building codes side-by-side"}
        subtitle={isAr ? "اختر حتى 4 أكواد لمقارنة سماكات الغطاء ونسب التسليح وتصنيف الحريق وقيم العزل." : "Pick up to 4 codes to compare concrete cover, rebar ratios, fire ratings and insulation limits."}
      />
      <Card className="mb-5">
        <div className="flex flex-wrap gap-2">
          {codes.map((c) => (
            <button
              key={c.code_id}
              onClick={() => toggle(c.code_id)}
              data-testid={`compare-toggle-${c.code_id}`}
              className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${selected.includes(c.code_id) ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/40"}`}
            >
              {isAr ? c.name_ar : c.name}
            </button>
          ))}
        </div>
      </Card>

      <Card className="overflow-x-auto p-0">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b border-border bg-white/5">
              <th className="sticky start-0 bg-card px-4 py-3 text-start">{isAr ? "المعيار" : "Metric"}</th>
              {cols.map((p) => (
                <th key={p.code_id} className="px-4 py-3 text-center">
                  <div className="font-black text-primary">{p.code_id.replace(/_/g, " ")}</div>
                  <div className="text-[10px] font-normal text-muted-foreground">{p.region}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map(([path, en, ar, fmt]) => {
              const vals = cols.map((p) => dig(p, path));
              const uniq = new Set(vals.map((v) => String(v)));
              const differs = uniq.size > 1;
              return (
                <tr key={path} className="border-b border-border/40 last:border-0">
                  <td className="sticky start-0 bg-card px-4 py-2.5 font-semibold text-muted-foreground">{isAr ? ar : en}</td>
                  {vals.map((v, i) => (
                    <td key={i} className={`px-4 py-2.5 text-center font-bold ${differs ? "text-amber-300" : "text-foreground"}`}>
                      {v == null ? "—" : fmt(v)}
                    </td>
                  ))}
                </tr>
              );
            })}
            <tr className="border-t border-border">
              <td className="sticky start-0 bg-card px-4 py-2.5 font-semibold text-muted-foreground">{isAr ? "الأنظمة المسموحة" : "Systems allowed"}</td>
              {cols.map((p) => (
                <td key={p.code_id} className="px-4 py-2.5 text-center text-xs">{(p.systems_allowed || []).join(" · ")}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </Card>
      <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground"><GitCompare size={13} /> {isAr ? "القيم المميّزة بالأصفر تختلف بين الأكواد المختارة." : "Amber-highlighted values differ across the selected codes."}</p>
    </div>
  );
}
